import React from "react";
import { motion } from "motion/react";
import { Mail, MessageSquare } from "lucide-react";
import ContactForm from "../Components/ContactForm";

export default function Contact() {
  const [theme, setTheme] = React.useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  React.useEffect(() => {
    const handleStorage = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('storage', handleStorage);
    
    const handleThemeChange = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('themechange', handleThemeChange);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('themechange', handleThemeChange);
    };
  }, []);

  return (
    <div
      className={`min-h-screen ${
        theme === "dark" ? "bg-[#05070d] text-slate-100" : "bg-[#fff8f3] text-slate-900"
      }`}
    >
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-400 via-orange-500 to-rose-600 py-20 text-white md:py-28">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.35),transparent_50%)]" />
        <motion.div
          className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <Mail className="mx-auto mb-6 h-16 w-16 opacity-95" />
          <h1 className="font-display text-5xl font-semibold tracking-tight md:text-6xl">Get in touch</h1>
          <p className="mx-auto mt-4 max-w-3xl text-xl leading-relaxed text-white/90 md:text-2xl">
            Have questions about sun safety or our technology? We would love to hear from you.
          </p>
        </motion.div>
      </div>

      {/* Contact Form Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Left side - Info */}
          <div>
            <h2 className={`text-3xl font-bold mb-6 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Let's Start a Conversation
            </h2>
            <p className={`text-lg leading-relaxed mb-8 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              Whether you have questions about our AI-powered skin monitoring technology, 
              want to learn more about sun safety, or are interested in partnerships, 
              we're here to help.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  theme === 'dark' 
                    ? 'bg-orange-500/20 text-orange-400' 
                    : 'bg-orange-100 text-orange-600'
                }`}>
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h3 className={`font-semibold mb-1 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Email Us
                  </h3>
                  <a 
                    href="mailto:sunscreened26@gmail.com"
                    className={`text-orange-500 hover:text-orange-600 ${
                      theme === 'dark' ? 'hover:text-orange-400' : ''
                    }`}
                  >
                    sunscreened26@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  theme === 'dark' 
                    ? 'bg-coral-500/20 text-coral-400' 
                    : 'bg-coral-100 text-coral-600'
                }`}>
                  <MessageSquare className="w-6 h-6" />
                </div>
                <div>
                  <h3 className={`font-semibold mb-1 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Response Time
                  </h3>
                  <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                    We typically respond within 24-48 hours
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Form */}
          <div>
            <ContactForm />
          </div>
        </div>
      </div>
    </div>
  );
}

